import { getColor, getShadow } from "@aircall/tractor";
import styled, { css } from "@xstyled/styled-components";

import {
  LABEL_HEIGHT,
  MARKER_SIZE,
  SLIDER_HEIGHT,
  THUMB_SIZE,
} from "./Slider.constants";
import { SliderMarkProps } from "./Slider.decl";

function thumbStyle({
  disabled,
  hideThumb,
}: {
  disabled?: boolean;
  hideThumb?: boolean;
}) {
  if (hideThumb) {
    return css`
      // display: none; does not enable the thumb to be clickable again.
      opacity: 0;
    `;
  }

  if (disabled) {
    return css`
      cursor: not-allowed;
      background-color: surface-disabled;
      border: 4px solid ${getColor("graphic-disabled")};
    `;
  }

  return css`
    cursor: pointer;
    background-color: surface-interactive-primary;
    border: 4px solid ${getColor("graphic-contrast-light")};
  `;
}

export const SliderRange = styled.input<{
  progress: number;
  hideThumb: boolean;
}>`
  margin: 0;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  appearance: none;
  width: 100%;
  height: ${SLIDER_HEIGHT}px;

  ${({ progress, disabled }) => {
    if (progress === 0 || disabled) {
      return css`
        background-color: graphic-default;
      `;
    }

    return css`
      background: linear-gradient(
        to right,
        ${getColor("surface-interactive-primary")} ${progress}%,
        ${getColor("graphic-default")} ${progress}%
      );
    `;
  }}

  border-radius: 16px;
  outline: none;
  &::-webkit-slider-thumb {
    // We need to adjust the thumb to the center of the slider because of the usage of <datalist id="markers">
    ${({ list }) =>
      list
        ? css`
            transform: translate(0, -${THUMB_SIZE / 2 - SLIDER_HEIGHT / 2}px);
          `
        : null}
    appearance: none;
    width: ${THUMB_SIZE}px;
    height: ${THUMB_SIZE}px;
    box-shadow: ${getShadow(4)};
    border-radius: 50%;

    ${thumbStyle}
  }
`;

function markStyle({ state, disabled, hideThumb }: SliderMarkProps) {
  return css`
    &:before {
      content: "";
      top: ${LABEL_HEIGHT + THUMB_SIZE / 2}px;
      position: absolute;
      width: ${MARKER_SIZE}px;
      height: ${MARKER_SIZE}px;
      background-color: ${
        state === "Greater" || disabled || hideThumb
          ? "graphic-neutral"
          : "graphic-contrast-light"
      };
      border-radius: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 1;
      pointer-events: none;
    }
  `;
}

export const SliderMark = styled.option.attrs<SliderMarkProps>(({ left }) => ({
  style: { left },
}))<SliderMarkProps>`
  ${({ theme }) => theme.typography.bodyRegularS};
  position: absolute;
  height: 2px;
  color: ${({ disabled }) =>
    getColor(disabled ? "text-disabled" : "text-base")};
  border-radius: 50%;
  width: min-content;
  transform: translate(-50%, 0);
  ${markStyle}
`;

export const SliderDataList = styled.datalist`
  height: ${LABEL_HEIGHT}px;
  position: relative;
  display: flex;
  flex-direction: row;
  // this horizontal margin is to align the marks with the labels
  margin: 0 ${THUMB_SIZE / 2}px;
`;
